import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Star, ArrowRight, Sparkles, GraduationCap } from 'lucide-react';
import { PurchaseButton } from '@/components/CheckoutModal';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1592478411213-6153e4ebc07d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxWUiUyMGhlYWRzZXQlMjBlZHVjYXRpb24lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc4NjY3MDMzMXww&ixlib=rb-4.1.0&q=85&w=1200";
const VR_KIDS = "https://images.unsplash.com/photo-1576633587382-13ddf37b1fc1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjaGlsZHJlbiUyMFZSJTIwaGVhZHNldCUyMGltbWVyc2l2ZSUyMGxlYXJuaW5nJTIwZXhwZXJpZW5jZXxlbnwwfHx8fDE3ODY3MjkxNjJ8MA&ixlib=rb-4.1.0&q=85&w=800";
const VR_GROUP = "https://images.unsplash.com/photo-1538388149542-5e24932d11a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxjaGlsZHJlbiUyMFZSJTIwaGVhZHNldCUyMGltbWVyc2l2ZSUyMGxlYXJuaW5nJTIwZXhwZXJpZW5jZXxlbnwwfHx8fDE3ODY3MjkxNjJ8MA&ixlib=rb-4.1.0&q=85&w=800";

const MembershipCard = ({ membership, index }) => {
  const isPremium = membership.title === 'All-American';
  const isStarter = membership.title === 'Matrix Rookie';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className={`relative p-6 rounded-2xl flex flex-col h-full tech-bracket ${
        isPremium ? 'glass-light ring-2 ring-cta' : 'glass-light'
      }`}
      data-testid={`membership-card-${membership.id}`}
    >
      {isPremium && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 rounded-full bg-cta text-black text-sm font-semibold flex items-center gap-1">
            <Sparkles className="w-4 h-4" /> Premium
          </span>
        </div>
      )}
      {isStarter && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-4 py-1 rounded-full bg-green-500 text-white text-sm font-semibold">Start Here</span>
        </div>
      )}

      <div className="text-center mb-5">
        <h3 className="text-xl font-serif font-bold text-white mb-2">{membership.title}</h3>
        <div className="mb-3">
          <span className="text-3xl font-bold text-cta">{membership.price}</span>
          <span className="text-muted-foreground text-sm"> / year</span>
        </div>
        <p className="text-muted-foreground text-sm">{membership.description}</p>
      </div>

      <div className="flex-1 mb-5">
        <h4 className="text-sm font-semibold text-white mb-3">Includes:</h4>
        <ul className="space-y-2">
          {membership.includes?.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>

      <PurchaseButton
        item={membership}
        itemType="membership"
        className={`w-full flex items-center justify-center gap-2 py-3 px-6 rounded-full font-semibold transition-all ${
          isPremium ? 'bg-cta text-black hover:bg-cta/90' : ''
        }`}
      />
    </motion.div>
  );
};

export default function CoursesPage() {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/courses`).then(res => { setMemberships(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const sorted = [...memberships].sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen pt-24" data-testid="courses-page">
      {/* Hero */}
      <section className="py-12 md:py-20 relative overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-2 mb-4">
                <GraduationCap className="w-6 h-6 text-cta" />
                <span className="text-cta font-semibold">Matrix University Memberships</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-testid="courses-title">
                More Than a Headset.<br />Enter the Experience.
              </h1>
              <p className="text-lg text-muted-foreground mb-4">
                Members don't simply purchase technology — they become part of The Matrix University, an evolving educational, technology, and innovation ecosystem.
              </p>
              <p className="text-cta font-semibold text-lg">Membership starts at $500/year</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-primary/10">
                  <img src={VR_KIDS} alt="Youth VR learning" className="w-full h-48 md:h-64 object-cover" />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-primary/10 mt-8">
                  <img src={VR_GROUP} alt="VR group experience" className="w-full h-48 md:h-64 object-cover" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Membership Grid */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="p-8 rounded-2xl glass-light animate-pulse h-96">
                  <div className="h-8 bg-muted rounded mb-4" />
                  <div className="h-12 bg-muted rounded mb-4" />
                  <div className="space-y-3">{[1,2,3].map(j => <div key={j} className="h-4 bg-muted rounded" />)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {sorted.map((m, i) => <MembershipCard key={m.id} membership={m} index={i} />)}
            </div>
          )}
        </div>
      </section>

      {/* Member Journey */}
      <section className="py-12 md:py-16 bg-card/50">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="rounded-2xl overflow-hidden glass-light">
                <img src={VR_KIDS} alt="VR Education in action" className="w-full h-64 md:h-80 object-cover" />
              </div>
            </motion.div>
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">The Matrix Member Journey</h2>
                <p className="text-muted-foreground">Your pathway from exploration to premium membership.</p>
              </motion.div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { step: "$500", label: "ENTER", desc: "Join the community and begin exploring" },
              { step: "$1,200", label: "EXPERIENCE", desc: "Meta Quest headset + VictoryXR experiences" },
              { step: "$1,250+", label: "SPECIALIZE", desc: "Choose History, Science, CTE, Finance, or AI" },
              { step: "$2,500", label: "ALL-AMERICAN", desc: "Premium membership with everything included" },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="p-4 rounded-xl glass-light text-center">
                <div className="text-xl font-bold text-cta mb-1">{item.step}</div>
                <div className="text-white font-semibold tracking-wider text-xs mb-1">{item.label}</div>
                <p className="text-muted-foreground text-xs">{item.desc}</p>
              </motion.div>
            ))}
          </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl mx-auto text-center p-8 rounded-2xl glass-light">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">Need More Information?</h2>
            <p className="text-lg text-muted-foreground mb-8">Contact us to learn more about memberships, institutional packages, or custom solutions.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2">
                Request More Information <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" className="btn-secondary inline-flex items-center justify-center gap-2">
                View Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
